import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  User as FirebaseUser,
  GoogleAuthProvider,
  signInWithPopup,
  updateProfile,
} from "firebase/auth";
import { auth, storage } from "./config";
import { User } from "../types";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";

// Convert Firebase user to our User type
const convertFirebaseUser = (firebaseUser: FirebaseUser): User => ({
  id: firebaseUser.uid,
  email: firebaseUser.email || "",
  name: firebaseUser.displayName || firebaseUser.email?.split("@")[0] || "User",
  photoURL: firebaseUser.photoURL || undefined,
});

// Email/Password Authentication
export const signInWithEmail = async (email: string, password: string): Promise<User> => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    return convertFirebaseUser(userCredential.user);
  } catch (error) {
    throw new Error("Invalid email or password");
  }
};

export const signUpWithEmail = async (email: string, password: string, name: string): Promise<User> => {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    return convertFirebaseUser(userCredential.user);
  } catch (error: any) {
    if (error.code === "auth/email-already-in-use") {
      throw new Error("Email already in use");
    }
    throw new Error("Failed to create account");
  }
};

// Google Authentication
export const signInWithGoogle = async (): Promise<User> => {
  try {
    const provider = new GoogleAuthProvider();
    const userCredential = await signInWithPopup(auth, provider);
    return convertFirebaseUser(userCredential.user);
  } catch (error) {
    throw new Error("Failed to sign in with Google");
  }
};

// Sign Out
export const signOutUser = async (): Promise<void> => {
  try {
    await signOut(auth);
  } catch (error) {
    throw new Error("Failed to sign out");
  }
};

// Auth State Observer
export const onAuthStateChange = (callback: (user: User | null) => void) => {
  return onAuthStateChanged(auth, (firebaseUser) => {
    if (firebaseUser) {
      callback(convertFirebaseUser(firebaseUser));
    } else {
      callback(null);
    }
  });
};

// Get ID Token for authenticated requests
export const getIdToken = async (): Promise<string | null> => {
  try {
    await new Promise(resolve => setTimeout(resolve, 100));
    const currentUser = auth.currentUser;
    if (currentUser) {
      return await currentUser.getIdToken();
    }
    return null;
  } catch (error) {
    console.error('Error getting ID token:', error);
    return null;
  }
};

// Update user profile
export const updateUserProfile = async (displayName: string, photoURL?: string): Promise<void> => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error("No user is currently signed in");
    }

    const updateData: { displayName: string; photoURL?: string } = { displayName };
    if (photoURL) {
      updateData.photoURL = photoURL;
    }

    await updateProfile(currentUser, updateData);
  } catch (error) {
    console.error("Error updating profile:", error);
    throw new Error("Failed to update profile");
  }
};

// Upload profile photo
export const uploadProfilePhoto = async (file: File): Promise<string> => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error("No user is currently signed in");
    }

    // Create a reference to the file location
    const photoRef = ref(storage, `profile-photos/${currentUser.uid}/${Date.now()}_${file.name}`);
    
    // Upload the file
    await uploadBytes(photoRef, file);
    
    // Get the download URL
    const downloadURL = await getDownloadURL(photoRef);
    
    return downloadURL;
  } catch (error) {
    console.error("Error uploading profile photo:", error);
    throw new Error("Failed to upload profile photo");
  }
};
